import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchServiceProfitabilityReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ComposedChart,
  Bar,
  Line,
  ReferenceLine,
} from "recharts";

import "./ServiceProfitabilityReportPage.css";

// I18N NOTE
// ---------
// This page keeps backend payload keys and local classification codes stable.
// The React layer translates only user-facing labels, descriptions, chart
// legends, tooltips, empty states and local error messages. Backend
// methodology note_keys are now translated by the frontend, while the
// legacy backend notes array remains a safe fallback for older payloads.

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format money-like values with fixed 2 decimals.
 */
function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format minutes with one decimal place.
 */
function formatMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0";
  return n.toFixed(1);
}

/**
 * Format integer-like counters robustly.
 */
function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

/**
 * Format ratio 0..1 as percentage text.
 *
 * Example:
 * - 0.18 -> 18.0%
 */
function formatPercentFromRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${(n * 100).toFixed(1)}%`;
}

/**
 * Generic percentage formatter for already-percent values.
 */
function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${n.toFixed(1)}%`;
}

/* -------------------------------------------------------------------------- */
/* Chart palette                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Stable local palette for Service Profitability visual blocks.
 *
 * Meaning:
 * - profitability / revenue family: blue
 * - premium / strong efficiency accents: green
 * - secondary support / cumulative line: amber
 * - neutral frame: grays
 */
const CHART_COLORS = Object.freeze({
  revenue: "#2563eb",
  revenueLight: "#60a5fa",
  profitability: "#1d4ed8",
  premium: "#16a34a",
  cumulative: "#f59e0b",
  grid: "#e5e7eb",
  axis: "#6b7280",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d1d5db",
  textMain: "#111827",
  textMuted: "#6b7280",
});

/* -------------------------------------------------------------------------- */
/* Chart helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Bubble-size helper for profitability matrix.
 *
 * We intentionally keep the bubble radius in a visually stable band,
 * so one very large service does not destroy readability.
 */
function bubbleSizeFromRevenue(value, minRevenue, maxRevenue) {
  const v = Number(value) || 0;
  const min = Number(minRevenue) || 0;
  const max = Number(maxRevenue) || 0;

  if (max <= min) return 140;

  const ratio = (v - min) / (max - min);
  return 80 + ratio * 260;
}

/**
 * Determine a simple visual class for service dots.
 *
 * This is intentionally heuristic and managerial, not scientific.
 */
function getProfitabilityClass(row) {
  const visits = Number(row?.total_visits) || 0;
  const revenuePerHour = Number(row?.revenue_per_booked_hour) || 0;
  const totalRevenue = Number(row?.total_revenue) || 0;

  if (visits >= 8 && revenuePerHour >= 140) return "star";
  if (visits <= 3 && revenuePerHour >= 150) return "premium";
  if (visits >= 8 && revenuePerHour < 90) return "volume_low_efficiency";
  if (totalRevenue < 150 && visits <= 2) return "tail";
  return "standard";
}

/**
 * Human-readable label for profitability class.
 */
function getProfitabilityClassLabel(profitabilityClass, t) {
  switch (profitabilityClass) {
    case "star":
      return t("reports:serviceProfitability.classes.star");
    case "premium":
      return t("reports:serviceProfitability.classes.premium");
    case "volume_low_efficiency":
      return t("reports:serviceProfitability.classes.volumeLowEfficiency");
    case "tail":
      return t("reports:serviceProfitability.classes.tail");
    case "standard":
    default:
      return t("reports:serviceProfitability.classes.standard");
  }
}

function getProfitabilityColor(profitabilityClass) {
  switch (profitabilityClass) {
    case "star":
      return CHART_COLORS.premium;
    case "premium":
      return "#0ea5e9";
    case "volume_low_efficiency":
      return "#f59e0b";
    case "tail":
      return "#94a3b8";
    case "standard":
    default:
      return CHART_COLORS.revenue;
  }
}

/**
 * Short managerial interpretation for the profitability matrix tooltip.
 *
 * This is the key improvement:
 * the user should not have to guess what "star" or "premium niche" means.
 */
function getProfitabilityExplanation(row, t) {
  const profitabilityClass = String(row?.profitabilityClass || "").toLowerCase();

  switch (profitabilityClass) {
    case "star":
      return t("reports:serviceProfitability.explanations.star");
    case "premium":
      return t("reports:serviceProfitability.explanations.premium");
    case "volume_low_efficiency":
      return t("reports:serviceProfitability.explanations.volumeLowEfficiency");
    case "tail":
      return t("reports:serviceProfitability.explanations.tail");
    case "standard":
    default:
      return t("reports:serviceProfitability.explanations.standard");
  }
}

/**
 * Custom tooltip for the profitability matrix.
 *
 * Why custom tooltip is better here:
 * - bubble charts are harder to read than bars
 * - the user needs interpretation, not only raw numbers
 * - the service label and class meaning should be explicit
 */
function ProfitabilityMatrixTooltip({ active, payload, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{row.label}</div>

      <div className="chart-tooltip__badgeRow">
        <span className={`legend-chip legend-chip--inline legend-chip--${row.profitabilityClass || "standard"}`}>
          {getProfitabilityClassLabel(row.profitabilityClass, t)}
        </span>
        <span className="chart-tooltip__category">
          {row.primary_category || t("reports:serviceProfitability.unknownCategory")}
        </span>
      </div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:serviceProfitability.chart.visits")}</span>
          <strong>{formatCount(row.total_visits)}</strong>
        </div>
        <div>
          <span>{t("reports:serviceProfitability.totalRevenue")}</span>
          <strong>{formatMoney(row.total_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:serviceProfitability.sort.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
        <div>
          <span>{t("reports:serviceProfitability.sort.avgRevenuePerVisit")}</span>
          <strong>{formatMoney(row.avg_revenue_per_visit)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:serviceProfitability.totalBookedMinutes")}</span>
          <strong>{formatMinutes(row.total_booked_minutes)} min</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {getProfitabilityExplanation(row, t)}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const ServiceProfitabilityReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Role-based allowed evaluation modes.
   *
   * This follows the same direction as your other report pages:
   * - plain user -> month only
   * - elevated roles -> broader analytical freedom
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  /**
   * Shared evaluation period controller.
   */
  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Main page-level filters.
   *
   * - userIds is sent to backend
   * - searchText is local only
   * - sortBy / sortDir are local only
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    searchText: "",
    sortBy: "total_revenue",
    sortDir: "desc",
  }));

  /**
   * Available employees for elevated-role filtering.
   */
  const [users, setUsers] = useState([]);

  /**
   * Raw backend payload.
   */
  const [report, setReport] = useState(null);

  /**
   * Generic page state.
   */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Keep track of the actual loaded period.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === "user"                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[ServiceProfitabilityReportPage] Failed to fetch users:", err);
        setError(t("reports:serviceProfitability.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ------------------------------------------------------------------------ */
  /* Current-user display helper                                              */
  /* ------------------------------------------------------------------------ */

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report data                                                        */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchServiceProfitabilityReport(from, to, userIds);

        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[ServiceProfitabilityReportPage] Error fetching report:", err);
        setError(t("reports:serviceProfitability.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /* ------------------------------------------------------------------------ */
  /* Initial auto-load                                                        */
  /* ------------------------------------------------------------------------ */

  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived report sections                                                  */
  /* ------------------------------------------------------------------------ */

  const rows = useMemo(() => {
    return Array.isArray(report?.rows) ? report.rows : [];
  }, [report]);

  const methodologyNotes = useMemo(() => {
    const methodology = report?.methodology || {};
    const reportKind = methodology.report_kind || "service_profitability";

    /**
     * Preferred path for the i18n migration:
     * backend sends stable note_keys, frontend owns the visible language.
     * The legacy notes array remains supported so this page continues to work
     * against older backend payloads during staged deployments.
     */
    if (Array.isArray(methodology.note_keys) && methodology.note_keys.length > 0) {
      return methodology.note_keys.map((noteKey) => {
        const translatedNote = t(
          `reports:methodology.${reportKind}.notes.${noteKey}`,
          { defaultValue: "" }
        );

        return translatedNote || String(noteKey);
      });
    }

    return Array.isArray(methodology.notes) ? methodology.notes : [];
  }, [report, t]);

  const summary = report?.summary || {
    completed_visits_count: 0,
    distinct_services_count: 0,
    total_revenue: "0.00",
    total_booked_minutes: 0,
  };

  /* ------------------------------------------------------------------------ */
  /* Local search + sorting                                                   */
  /* ------------------------------------------------------------------------ */

  const filteredRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return rows;

    return rows.filter((row) => {
      const service = String(row.service || "").toLowerCase();
      const category = String(row.primary_category || "").toLowerCase();

      return service.includes(searchText) || category.includes(searchText);
    });
  }, [rows, filters.searchText]);

  const sortedRows = useMemo(() => {
    const out = [...filteredRows];
    const sortBy = filters.sortBy || "total_revenue";
    const sortDir = filters.sortDir === "asc" ? "asc" : "desc";

    out.sort((a, b) => {
      let cmp = 0;

      switch (sortBy) {
        case "service":
          cmp = String(a.service || "").localeCompare(String(b.service || ""));
          break;

        case "revenue_per_booked_hour":
          cmp =
            Number(a.revenue_per_booked_hour || 0) -
            Number(b.revenue_per_booked_hour || 0);
          break;

        case "avg_revenue_per_visit":
          cmp =
            Number(a.avg_revenue_per_visit || 0) -
            Number(b.avg_revenue_per_visit || 0);
          break;

        case "total_visits":
          cmp = Number(a.total_visits || 0) - Number(b.total_visits || 0);
          break;

        case "total_booked_minutes":
          cmp =
            Number(a.total_booked_minutes || 0) -
            Number(b.total_booked_minutes || 0);
          break;

        case "total_revenue":
        default:
          cmp = Number(a.total_revenue || 0) - Number(b.total_revenue || 0);
          break;
      }

      if (cmp === 0) {
        cmp = String(a.service || "").localeCompare(String(b.service || ""));
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return out;
  }, [filteredRows, filters.sortBy, filters.sortDir]);

  /* ------------------------------------------------------------------------ */
  /* Top cards                                                                */
  /* ------------------------------------------------------------------------ */

  const strongestRevenueService =
    summary?.top_revenue_service ||
    [...rows].sort(
      (a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0)
    )[0] ||
    null;

  const strongestHourlyService =
    summary?.top_revenue_per_hour_service ||
    [...rows].sort(
      (a, b) =>
        Number(b.revenue_per_booked_hour || 0) -
        Number(a.revenue_per_booked_hour || 0)
    )[0] ||
    null;

  const strongestAvgTicketService =
    summary?.top_average_ticket_service ||
    [...rows].sort(
      (a, b) =>
        Number(b.avg_revenue_per_visit || 0) -
        Number(a.avg_revenue_per_visit || 0)
    )[0] ||
    null;

  /* ------------------------------------------------------------------------ */
  /* Chart data                                                               */
  /* ------------------------------------------------------------------------ */

  /**
   * Normalize service rows from the backend.
   *
   * We keep this defensive because field naming may evolve slightly
   * while the page is under active development.
   */
  const serviceRows = useMemo(() => {
    const rawRows = Array.isArray(report?.rows)
      ? report.rows
      : Array.isArray(report?.service_rows)
      ? report.service_rows
      : [];

    return rawRows.map((row, index) => ({
      id:
        row?.service_id ??
        row?.id ??
        `${row?.service || row?.name || "service"}-${index}`,

      label: row?.service || row?.service_name || row?.name || "Unknown service",
      primary_category: row?.primary_category || "Unknown",

      total_visits: Number(row?.total_visits || 0),
      total_revenue: Number(row?.total_revenue || 0),
      total_booked_minutes: Number(row?.total_booked_minutes || 0),
      avg_revenue_per_visit: Number(row?.avg_revenue_per_visit || 0),
      revenue_per_booked_hour: Number(row?.revenue_per_booked_hour || 0),
    }));
  }, [report]);

  /**
   * Profitability matrix data.
   *
   * X = total visits
   * Y = revenue per booked hour
   * bubble size = total revenue
   */
  const profitabilityMatrixData = useMemo(() => {
    if (!serviceRows.length) return [];

    const revenues = serviceRows.map((row) => Number(row.total_revenue) || 0);
    const minRevenue = Math.min(...revenues);
    const maxRevenue = Math.max(...revenues);

    return serviceRows.map((row) => {
      const profitabilityClass = getProfitabilityClass(row);

      return {
        ...row,
        x: row.total_visits,
        y: row.revenue_per_booked_hour,
        z: bubbleSizeFromRevenue(row.total_revenue, minRevenue, maxRevenue),
        profitabilityClass,
        color: getProfitabilityColor(profitabilityClass),
      };
    });
  }, [serviceRows]);

  /**
   * Pareto chart data.
   *
   * We sort services by total revenue descending and then compute
   * cumulative revenue share percentage.
   */
  const paretoRevenueData = useMemo(() => {
    const sorted = [...serviceRows].sort(
      (a, b) =>
        Number(b.total_revenue || 0) - Number(a.total_revenue || 0) ||
        String(a.label || "").localeCompare(String(b.label || ""))
    );

    const totalRevenue = sorted.reduce(
      (sum, row) => sum + (Number(row.total_revenue) || 0),
      0
    );

    let runningRevenue = 0;

    return sorted.slice(0, 20).map((row) => {
      runningRevenue += Number(row.total_revenue || 0);

      const cumulativeRevenueSharePercent =
        totalRevenue > 0 ? (runningRevenue / totalRevenue) * 100 : 0;

      return {
        ...row,
        cumulative_revenue_share_percent: cumulativeRevenueSharePercent,
      };
    });
  }, [serviceRows]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="service-profitability-page">
      <div className="service-profitability-header">
        <div className="header-left">
          <h1>{t("reports:serviceProfitability.title")}</h1>
          <p className="header-hint">{t("reports:serviceProfitability.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:serviceProfitability.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-sort">
            <label className="filter-label">{t("reports:serviceProfitability.sortBy")}</label>
            <select
              className="sort-select"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: e.target.value,
                }))
              }
            >
              <option value="total_revenue">{t("reports:serviceProfitability.sort.totalRevenue")}</option>
              <option value="revenue_per_booked_hour">{t("reports:serviceProfitability.sort.revenuePerBookedHour")}</option>
              <option value="avg_revenue_per_visit">{t("reports:serviceProfitability.sort.avgRevenuePerVisit")}</option>
              <option value="total_visits">{t("reports:serviceProfitability.sort.totalVisits")}</option>
              <option value="total_booked_minutes">{t("reports:serviceProfitability.sort.totalBookedMinutes")}</option>
              <option value="service">{t("reports:serviceProfitability.sort.serviceName")}</option>
            </select>
          </div>

          <div className="filter-item filter-item-sortdir">
            <label className="filter-label">{t("reports:serviceProfitability.direction")}</label>
            <select
              className="sort-select"
              value={filters.sortDir}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortDir: e.target.value,
                }))
              }
            >
              <option value="desc">{t("reports:serviceProfitability.sort.descending")}</option>
              <option value="asc">{t("reports:serviceProfitability.sort.ascending")}</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : report ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:serviceProfitability.completedVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.completed_visits_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:serviceProfitability.distinctServices")}</span>
                <span className="summary-value">
                  {formatCount(summary.distinct_services_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:serviceProfitability.totalRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:serviceProfitability.totalBookedMinutes")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_booked_minutes)}
                </span>
              </div>
            </div>

            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:serviceProfitability.topByRevenue")}</div>
                {strongestRevenueService ? (
                  <>
                    <div className="highlight-name">{strongestRevenueService.service}</div>
                    <div className="highlight-metric">
                      {formatMoney(strongestRevenueService.total_revenue)} PLN
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:serviceProfitability.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:serviceProfitability.topByRevenuePerBookedHour")}</div>
                {strongestHourlyService ? (
                  <>
                    <div className="highlight-name">{strongestHourlyService.service}</div>
                    <div className="highlight-metric">
                      {formatMoney(strongestHourlyService.revenue_per_booked_hour)} PLN/h
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:serviceProfitability.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:serviceProfitability.topByAverageTicket")}</div>
                {strongestAvgTicketService ? (
                  <>
                    <div className="highlight-name">{strongestAvgTicketService.service}</div>
                    <div className="highlight-metric">
                      {formatMoney(strongestAvgTicketService.avg_revenue_per_visit)} PLN/visit
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:serviceProfitability.noData")}</div>
                )}
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:serviceProfitability.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:serviceProfitability.profitabilityMatrixTitle")}</h2>
                  <p>{t("reports:serviceProfitability.profitabilityMatrixDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 10, right: 24, bottom: 12, left: 8 }}>
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        dataKey="x"
                        name={t("reports:serviceProfitability.chart.visits")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:serviceProfitability.chart.totalVisits"),
                          position: "insideBottom",
                          offset: -4,
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <YAxis
                        type="number"
                        dataKey="y"
                        name={t("reports:serviceProfitability.chart.revenuePerBookedHour")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:serviceProfitability.chart.revenuePerBookedHourPln"),
                          angle: -90,
                          position: "insideLeft",
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <ZAxis type="number" dataKey="z" range={[80, 340]} />

                      <Tooltip content={<ProfitabilityMatrixTooltip t={t} />} />

                      <Legend />

                      <ReferenceLine
                        y={120}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Scatter name={t("reports:serviceProfitability.chart.services")} data={profitabilityMatrixData}>
                        {profitabilityMatrixData.map((entry) => (
                          <Cell
                            key={`profitability-dot-${entry.id}`}
                            fill={entry.color}
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-note">
                  <span className="legend-chip legend-chip--star">{t("reports:serviceProfitability.classes.star")}</span>
                  <span className="legend-chip legend-chip--premium">{t("reports:serviceProfitability.classes.premium")}</span>
                  <span className="legend-chip legend-chip--volume">{t("reports:serviceProfitability.classes.volumeSofterEfficiency")}</span>
                  <span className="legend-chip legend-chip--tail">{t("reports:serviceProfitability.classes.tail")}</span>
                </div>
              </div>

              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:serviceProfitability.paretoRevenueChartTitle")}</h2>
                  <p>{t("reports:serviceProfitability.paretoRevenueChartDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={paretoRevenueData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 24 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 11 }}
                        angle={-20}
                        textAnchor="end"
                        interval={0}
                        height={72}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${value}%`}
                      />

                      <Tooltip
                        formatter={(value, name) => {
                          if (name === t("reports:serviceProfitability.chart.revenue")) {
                            return [`${formatMoney(value)} PLN`, name];
                          }

                          if (name === t("reports:serviceProfitability.chart.cumulativeShare")) {
                            return [formatPercent(value), name];
                          }

                          return [value, name];
                        }}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />

                      <Legend />

                      <ReferenceLine
                        yAxisId="right"
                        y={80}
                        stroke="#94a3b8"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Bar
                        yAxisId="left"
                        dataKey="total_revenue"
                        name={t("reports:serviceProfitability.chart.revenue")}
                        radius={[6, 6, 0, 0]}
                      >
                        {paretoRevenueData.map((entry, index) => (
                          <Cell
                            key={`pareto-bar-${entry.id || index}`}
                            fill={CHART_COLORS.revenue}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative_revenue_share_percent"
                        name={t("reports:serviceProfitability.chart.cumulativeShare")}
                        stroke={CHART_COLORS.cumulative}
                        strokeWidth={2}
                        dot={{ r: 2, fill: CHART_COLORS.cumulative }}
                        activeDot={{ r: 5, fill: CHART_COLORS.cumulative }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:serviceProfitability.serviceRankingTitle")}</h2>
                <p>{t("reports:serviceProfitability.serviceRankingDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:serviceProfitability.table.service")}</th>
                      <th>{t("reports:serviceProfitability.table.primaryCategory")}</th>
                      <th>{t("reports:serviceProfitability.table.categoryVariants")}</th>
                      <th>{t("reports:serviceProfitability.table.totalVisits")}</th>
                      <th>{t("reports:serviceProfitability.table.totalBookedMin")}</th>
                      <th>{t("reports:serviceProfitability.table.totalRevenuePln")}</th>
                      <th>{t("reports:serviceProfitability.table.avgRevenuePerVisitPln")}</th>
                      <th>{t("reports:serviceProfitability.table.avgDurationMin")}</th>
                      <th>{t("reports:serviceProfitability.table.revenuePerBookedHourPln")}</th>
                      <th>{t("reports:serviceProfitability.table.shareOfRevenue")}</th>
                      <th>{t("reports:serviceProfitability.table.shareOfBookedTime")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length > 0 ? (
                      sortedRows.map((row, idx) => (
                        <tr key={`${row.service}-${idx}`}>
                          <td>{row.service}</td>
                          <td>{row.primary_category}</td>
                          <td>{formatCount(row.category_variants_count)}</td>
                          <td>{formatCount(row.total_visits)}</td>
                          <td>{formatCount(row.total_booked_minutes)}</td>
                          <td>{formatMoney(row.total_revenue)}</td>
                          <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                          <td>{formatMinutes(row.avg_duration_minutes)}</td>
                          <td>{formatMoney(row.revenue_per_booked_hour)}</td>
                          <td>{formatPercentFromRatio(row.share_of_total_revenue)}</td>
                          <td>{formatPercentFromRatio(row.share_of_total_booked_minutes)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="empty-table-cell">
                          {t("reports:serviceProfitability.noServicesMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default ServiceProfitabilityReportPage;

